import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, Wand2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { QueryGroup } from "@/types/queryBuilder";

interface NaturalLanguageQueryDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onQueryGenerated: (query: QueryGroup) => void;
}

export function NaturalLanguageQueryDialog({
	open,
	onOpenChange,
	onQueryGenerated
}: NaturalLanguageQueryDialogProps) {
	const { t } = useTranslation();
	const { toast } = useToast();
	const [query, setQuery] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const handleSubmit = async () => {
		if (!query.trim()) return;

		setIsLoading(true);
		try {
			const { data, error } = await supabase.functions.invoke("translate-natural-query", {
				body: { query: query.trim() }
			});

			if (error) throw error;

			if (data?.success && data?.query) {
				onQueryGenerated(data.query);
				toast({
					title: t("queries.ai.success", "Query generated"),
					description: t("queries.ai.successDescription", "Your natural language query has been converted")
				});
				setQuery("");
				onOpenChange(false);
			} else {
				throw new Error(data?.error || "Failed to generate query");
			}
		} catch (error: any) {
			console.error("Error generating query:", error);
			toast({
				title: t("queries.ai.error", "Error"),
				description: error.message || t("queries.ai.errorDescription", "Failed to convert your query"),
				variant: "destructive"
			});
		} finally {
			setIsLoading(false);
		}
	};

	const examples = [
		t("queries.ai.example1", "Find developers with Python or JavaScript skills"),
		t("queries.ai.example2", "Show me candidates in Paris who are looking for a job"),
		t("queries.ai.example3", "Find senior engineers with SQL experience")
	];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Sparkles className="h-5 w-5 text-purple-500" />
						{t("queries.ai.title", "AI Query Builder")}
					</DialogTitle>
					<DialogDescription>
						{t("queries.ai.description", "Describe what you're looking for in plain language, and AI will create the query for you.")}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<Textarea
						placeholder={t("queries.ai.placeholder", "e.g., Find all developers with React skills who are currently looking for a job...")}
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						className="min-h-[120px] resize-none"
						disabled={isLoading}
					/>

					<div className="space-y-2">
						<p className="text-xs text-muted-foreground font-medium">
							{t("queries.ai.examples", "Try these examples:")}
						</p>
						<div className="flex flex-wrap gap-2">
							{examples.map((example, index) => (
								<button
									key={index}
									type="button"
									className="text-xs px-2 py-1 rounded-sm bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
									onClick={() => setQuery(example)}
									disabled={isLoading}
								>
									{example}
								</button>
							))}
						</div>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
						{t("common.cancel", "Cancel")}
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={!query.trim() || isLoading}
						className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
					>
						{isLoading ? (
							<>
								<Loader2 className="h-4 w-4 mr-2 animate-spin" />
								{t("queries.ai.generating", "Generating...")}
							</>
						) : (
							<>
								<Wand2 className="h-4 w-4 mr-2" />
								{t("queries.ai.generate", "Generate Query")}
							</>
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
